import React, { useState } from "react"
import { Plus, Calendar, Users, Clock, Video, MoreVertical } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useMeetings } from "@/hooks/useMeetings"
import { useAuth } from "@/hooks/useAuth"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { useNavigate } from "react-router-dom"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { CriarReuniaoModal } from "@/components/CriarReuniaoModal"
import { Layout } from "@/components/Layout"

export default function Reunioes() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const { meetings, isLoading, deleteMeeting } = useMeetings()
  const [showCreateModal, setShowCreateModal] = useState(false)

  const canManage = profile?.role === "sindico" || profile?.role === "administracao"

  const getStatusColor = (status: string) => {
    switch (status) {
      case "live": return "bg-green-500"
      case "scheduled": return "bg-blue-500"
      case "ended": return "bg-gray-500"
      default: return "bg-gray-500"
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case "live": return "Ao Vivo"
      case "scheduled": return "Agendada"
      case "ended": return "Finalizada"
      default: return "Desconhecido"
    }
  }

  const isUpcoming = (date: string) => {
    return new Date(date) > new Date()
  }

  const upcomingMeetings = meetings.filter(meeting => 
    meeting.status === "scheduled" && isUpcoming(meeting.scheduled_date)
  )
  
  const pastMeetings = meetings.filter(meeting => 
    meeting.status === "ended" || !isUpcoming(meeting.scheduled_date)
  )

  const liveMeetings = meetings.filter(meeting => meeting.status === "live")

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Reuniões Online</h1>
        </div>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Reuniões Online</h1>
            <p className="text-muted-foreground mt-2">
              Participe das reuniões e enquetes do condomínio
            </p>
          </div>
          {canManage && (
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Reunião
            </Button>
          )}
        </div>

        {/* Live Meetings */}
        {liveMeetings.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse mr-2" />
              Ao Vivo Agora
            </h2>
            <div className="grid gap-4">
              {liveMeetings.map(meeting => (
                <Card key={meeting.id} className="border-red-200 bg-red-50/50">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold">{meeting.title}</h3>
                          <Badge className={`${getStatusColor(meeting.status)} text-white`}>
                            {getStatusText(meeting.status)}
                          </Badge>
                        </div>
                        {meeting.description && (
                          <p className="text-muted-foreground mb-4">{meeting.description}</p>
                        )}
                        <div className="flex items-center gap-6 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(meeting.scheduled_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {format(new Date(meeting.scheduled_date), "HH:mm", { locale: ptBR })}
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            Máx. {meeting.max_participants} participantes
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          onClick={() => navigate(`/reunioes/${meeting.id}`)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Video className="w-4 h-4 mr-2" />
                          Entrar
                        </Button>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => deleteMeeting(meeting.id)}>
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Meetings */}
        {upcomingMeetings.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Próximas Reuniões</h2>
            <div className="grid gap-4">
              {upcomingMeetings.map(meeting => (
                <Card key={meeting.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold">{meeting.title}</h3>
                          <Badge className={`${getStatusColor(meeting.status)} text-white`}>
                            {getStatusText(meeting.status)}
                          </Badge>
                        </div>
                        {meeting.description && (
                          <p className="text-muted-foreground mb-4">{meeting.description}</p>
                        )}
                        <div className="flex items-center gap-6 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(meeting.scheduled_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {format(new Date(meeting.scheduled_date), "HH:mm", { locale: ptBR })}
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            Máx. {meeting.max_participants} participantes
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/reunioes/${meeting.id}`)}
                        >
                          Ver Detalhes
                        </Button>
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => deleteMeeting(meeting.id)}>
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Past Meetings */}
        {pastMeetings.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">Reuniões Anteriores</h2>
            <div className="grid gap-4">
              {pastMeetings.map(meeting => (
                <Card key={meeting.id} className="opacity-75">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold">{meeting.title}</h3>
                          <Badge className={`${getStatusColor(meeting.status)} text-white`}>
                            {getStatusText(meeting.status)}
                          </Badge>
                        </div>
                        {meeting.description && (
                          <p className="text-muted-foreground mb-4">{meeting.description}</p>
                        )}
                        <div className="flex items-center gap-6 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(meeting.scheduled_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {format(new Date(meeting.scheduled_date), "HH:mm", { locale: ptBR })}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/reunioes/${meeting.id}`)}
                        >
                          Ver Resumo
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {meetings.length === 0 && (
          <div className="text-center py-12">
            <Video className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Nenhuma reunião agendada</h3>
            <p className="text-muted-foreground mb-6">
              {canManage 
                ? "Crie sua primeira reunião online com enquetes interativas."
                : "Aguarde o síndico agendar uma reunião."
              }
            </p>
            {canManage && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeira Reunião
              </Button>
            )}
          </div>
        )}

        {showCreateModal && (
          <CriarReuniaoModal 
            open={showCreateModal}
            onOpenChange={setShowCreateModal}
          />
        )}
      </div>
    </Layout>
  )
}